/**
 * GA4 helpers. Measurement ID must match `public/index.html` gtag script `id=` query.
 * Override per environment with REACT_APP_GA_MEASUREMENT_ID.
 */
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const DEFAULT_MEASUREMENT_ID = 'G-LWBMCVPLQE'

export const getGaMeasurementId = (): string =>
  (process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID).trim()

export const isGoogleAnalyticsAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.gtag === 'function'

export const trackGaPageView = (pagePath: string, pageTitle?: string): void => {
  const measurementId = getGaMeasurementId()
  if (!measurementId || !isGoogleAnalyticsAvailable()) return

  window.gtag?.('config', measurementId, {
    page_path: pagePath,
    page_title: pageTitle ?? document.title,
    page_location: window.location.href,
  })
}

type GaEventParams = Record<string, string | number | boolean | undefined>

export const trackGaEvent = (eventName: string, params?: GaEventParams): void => {
  if (!getGaMeasurementId() || !isGoogleAnalyticsAvailable()) return
  window.gtag?.('event', eventName, params ?? {})
}

/** Successful account creation (mark as conversion in GA4 Admin). */
export const trackGaSignUp = (
  method: 'email' | 'google',
  utmParams?: Record<string, string>,
): void => {
  trackGaEvent('sign_up', { method, ...utmParams })
}

/** Successful newsletter subscription. */
export const trackGaNewsletterSubscribe = (): void => {
  trackGaEvent('subscribe', { method: 'newsletter_page' })
}

/** Successful contact / lead form (mark as conversion in GA4 Admin). */
export const trackGaLeadFormSubmit = (
  formType: 'guest_contact' | 'investor_contact' | 'business_contact',
): void => {
  trackGaEvent('generate_lead', { form_type: formType })
}
